import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const translations = {
  en: {
    backToHome: 'Back to Home',
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: January 1, 2025',
    section1Title: '1. Introduction',
    section1Content: 'Rick Club Investment LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our investment services.',
    section2Title: '2. Information We Collect',
    section2Intro: 'We may collect information about you in various ways, including:',
    section2Items: [
      { label: 'Personal Data:', text: 'Name, email address, phone number, mailing address, and financial information you provide when inquiring about or making investments.' },
      { label: 'Financial Information:', text: 'Investment amounts, bank account details, and transaction history related to your investments with us.' },
      { label: 'Usage Data:', text: 'Information about how you access and use our website, including IP address, browser type, and pages visited.' },
      { label: 'Communication Data:', text: 'Records of correspondence when you contact us via email, phone, or our contact form.' },
    ],
    section3Title: '3. How We Use Your Information',
    section3Intro: 'We use the information we collect to:',
    section3Items: [
      'Process and manage your investment accounts',
      'Communicate with you about your investments and our services',
      'Send you updates, newsletters, and marketing materials (with your consent)',
      'Comply with legal and regulatory requirements',
      'Improve our website and services',
      'Prevent fraud and ensure security',
    ],
    section4Title: '4. Information Sharing',
    section4Content: 'We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our business, such as notaries, legal advisors, and banking institutions, solely for the purpose of facilitating your investments. All third parties are bound by confidentiality agreements.',
    section5Title: '5. Data Security',
    section5Content: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All investment documents are secured through public notary supervision and banking oversight.',
    section6Title: '6. Your Rights',
    section6Intro: 'You have the right to:',
    section6Items: [
      'Access the personal information we hold about you',
      'Request correction of inaccurate information',
      'Request deletion of your information (subject to legal requirements)',
      'Opt-out of marketing communications',
      'Withdraw consent where applicable',
    ],
    section7Title: '7. Contact Us',
    section7Intro: 'If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at:',
    contactCompany: 'Rick Club Investment LLC',
    contactEmail: 'Email: rickclub0516@gmail.com',
    contactPhone: 'Phone: +1 (214) 815-4829',
    contactLocation: 'Location: 320 Deker Dr, Irving, TX 75062',
    footerCopyright: '© 2025 Rick Club Investment LLC. All rights reserved.',
  },
  es: {
    backToHome: 'Volver al Inicio',
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: 1 de enero de 2025',
    section1Title: '1. Introducción',
    section1Content: 'Rick Club Investment LLC ("nosotros", "nuestro" o "nos") está comprometido a proteger su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su información cuando visita nuestro sitio web o utiliza nuestros servicios de inversión.',
    section2Title: '2. Información que Recopilamos',
    section2Intro: 'Podemos recopilar información sobre usted de varias maneras, incluyendo:',
    section2Items: [
      { label: 'Datos Personales:', text: 'Nombre, dirección de correo electrónico, número de teléfono, dirección postal e información financiera que proporciona al consultar o realizar inversiones.' },
      { label: 'Información Financiera:', text: 'Montos de inversión, detalles de cuentas bancarias e historial de transacciones relacionadas con sus inversiones con nosotros.' },
      { label: 'Datos de Uso:', text: 'Información sobre cómo accede y utiliza nuestro sitio web, incluida la dirección IP, el tipo de navegador y las páginas visitadas.' },
      { label: 'Datos de Comunicación:', text: 'Registros de correspondencia cuando nos contacta por correo electrónico, teléfono o nuestro formulario de contacto.' },
    ],
    section3Title: '3. Cómo Usamos su Información',
    section3Intro: 'Utilizamos la información que recopilamos para:',
    section3Items: [
      'Procesar y administrar sus cuentas de inversión',
      'Comunicarnos con usted sobre sus inversiones y nuestros servicios',
      'Enviarle actualizaciones, boletines y materiales de marketing (con su consentimiento)',
      'Cumplir con requisitos legales y regulatorios',
      'Mejorar nuestro sitio web y servicios',
      'Prevenir fraudes y garantizar la seguridad',
    ],
    section4Title: '4. Compartir Información',
    section4Content: 'No vendemos, intercambiamos ni alquilamos su información personal a terceros. Podemos compartir su información con proveedores de servicios de confianza que nos ayudan a operar nuestro negocio, como notarios, asesores legales e instituciones bancarias, únicamente con el propósito de facilitar sus inversiones. Todos los terceros están sujetos a acuerdos de confidencialidad.',
    section5Title: '5. Seguridad de Datos',
    section5Content: 'Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal contra el acceso no autorizado, alteración, divulgación o destrucción. Todos los documentos de inversión están asegurados mediante supervisión notarial pública y supervisión bancaria.',
    section6Title: '6. Sus Derechos',
    section6Intro: 'Usted tiene derecho a:',
    section6Items: [
      'Acceder a la información personal que tenemos sobre usted',
      'Solicitar la corrección de información inexacta',
      'Solicitar la eliminación de su información (sujeto a requisitos legales)',
      'Optar por no recibir comunicaciones de marketing',
      'Retirar el consentimiento cuando corresponda',
    ],
    section7Title: '7. Contáctenos',
    section7Intro: 'Si tiene preguntas sobre esta Política de Privacidad o desea ejercer sus derechos, contáctenos en:',
    contactCompany: 'Rick Club Investment LLC',
    contactEmail: 'Correo electrónico: rickclub0516@gmail.com',
    contactPhone: 'Teléfono: +1 (214) 815-4829',
    contactLocation: 'Ubicación: 320 Deker Dr, Irving, TX 75062',
    footerCopyright: '© 2025 Rick Club Investment LLC. Todos los derechos reservados.',
  },
};

export default function PrivacyPolicy() {
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const t = translations[language];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 font-body">
      {/* Navigation */}
      <nav className="bg-stone-950 shadow-2xl shadow-amber-900/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-500 blur-lg opacity-50"></div>
                <img src="https://static.readdy.ai/image/e468a762cbf2c4de3f207872d8ecdaa7/0b3887cade0bf729b27dae59d525fd13.png" alt="Rick Club Investment" className="h-12 w-auto relative" />
              </div>
              <span className="text-xl font-bold text-white font-sans">Rick Club Investment</span>
            </Link>
            <div className="flex items-center space-x-6">
              {/* Language Toggle */}
              <div className="flex items-center bg-stone-800 rounded-full p-1">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    language === 'en'
                      ? 'bg-amber-500 text-stone-900'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    language === 'es'
                      ? 'bg-amber-500 text-stone-900'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Español
                </button>
              </div>
              <Link to="/" className="text-gray-300 hover:text-amber-400 transition-colors flex items-center space-x-2 cursor-pointer">
                <i className="ri-arrow-left-line"></i>
                <span>{t.backToHome}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-stone-950 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white font-sans mb-4">{t.title}</h1>
          <p className="text-gray-400">{t.lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
          <div className="prose prose-lg max-w-none">
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section1Title}</h2>
              <p className="text-gray-600 leading-relaxed">
                {t.section1Content}
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section2Title}</h2>
              <p className="text-gray-600 leading-relaxed mb-4">{t.section2Intro}</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                {t.section2Items.map((item, index) => (
                  <li key={index}><strong>{item.label}</strong> {item.text}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section3Title}</h2>
              <p className="text-gray-600 leading-relaxed mb-4">{t.section3Intro}</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                {t.section3Items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section4Title}</h2>
              <p className="text-gray-600 leading-relaxed">
                {t.section4Content}
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section5Title}</h2>
              <p className="text-gray-600 leading-relaxed">
                {t.section5Content}
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section6Title}</h2>
              <p className="text-gray-600 leading-relaxed mb-4">{t.section6Intro}</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                {t.section6Items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-stone-900 font-sans mb-4">{t.section7Title}</h2>
              <p className="text-gray-600 leading-relaxed">
                {t.section7Intro}
              </p>
              <div className="mt-4 bg-stone-50 rounded-xl p-6">
                <p className="text-stone-900 font-semibold">{t.contactCompany}</p>
                <p className="text-gray-600">{t.contactEmail}</p>
                <p className="text-gray-600">{t.contactPhone}</p>
                <p className="text-gray-600">{t.contactLocation}</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-stone-900 text-gray-300 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-500">{t.footerCopyright}</p>
        </div>
      </footer>
    </div>
  );
}
